/**
 * The expiry schedule — every term that ends, in order, with no horizon.
 *
 * The pipeline report answers "what is coming in the next quarter"; this one answers "show me the
 * whole schedule", which is the question somebody planning next year's renewals asks. They read
 * the same tenancies through `listLeases`, so the two cannot disagree about a date.
 *
 * The chart is a count per bucket rather than a date axis, because the buckets are the decision:
 * already over, this month, this quarter, later. `expiryBucket()` in core decides which is which,
 * here as everywhere else in the product.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EXPIRY_BUCKETS } from "@rentorng/core";
import { api, downloadCsv } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { CompositionBar, SegmentKey } from "@/components/charts";
import { ExpiryBadge } from "@/components/status";
import { Button, Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, ReportHeader, ReportState, useCompactMoney } from "./shared";

interface Expiries {
  rows: Array<{
    id: string;
    reference: string;
    tenantName: string;
    unitLabel: string;
    propertyName: string;
    endDate: string | null;
    daysLeft: number;
    bucket: string;
    rentAmount: number;
  }>;
  asOf: string;
}

/**
 * How each of core's five expiry buckets is drawn.
 *
 * **The buckets and their names come from `EXPIRY_BUCKETS`** — the same five the dashboard and the
 * tenancy list use — because a report that invented its own would be a sixth definition of "due
 * soon" in a product whose whole promise is that nobody hears about an expiry late.
 *
 * All this adds is the colour, and it is an ordered scale rather than a categorical one: sooner is
 * more urgent, so the steps fall in lightness and the sequence reads with the colour removed. The
 * bar draws them furthest-away first so the darkest, most urgent band ends up at the right-hand
 * end, where the eye finishes.
 */
const BUCKET_STYLE: Record<string, { colour: string; texture?: "hatch" | "grid" }> = {
  later: { colour: "#c4cfcd" },
  d61_90: { colour: "#f59e0b" },
  d31_60: { colour: "#d97706", texture: "hatch" },
  d0_30: { colour: "#dc2626" },
  expired: { colour: "#991b1b", texture: "grid" },
};

/** Furthest away first — see the note above. */
const BUCKET_ORDER = ["later", "d61_90", "d31_60", "d0_30", "expired"] as const;

export function ExpiriesReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-expiries"],
    queryFn: () => api.get<Expiries>("/reports/expiries"),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Expiry schedule" question={`Every ${vocab.lease} with an end date, soonest first.`} />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);

  /* Counted from the rows the table prints, which is the only array on this page. The server sent
     each row's bucket, so nothing here decides what "within 30 days" means. */
  const counts = new Map<string, number>();
  for (const row of data.rows) counts.set(row.bucket, (counts.get(row.bucket) ?? 0) + 1);
  const segments = BUCKET_ORDER.map((key) => ({
    label: EXPIRY_BUCKETS.find((bucket) => bucket.key === key)!.label,
    value: counts.get(key) ?? 0,
    colour: BUCKET_STYLE[key].colour,
  }));

  const expired = counts.get("expired") ?? 0;
  const soon = (counts.get("d0_30") ?? 0) + (counts.get("d31_60") ?? 0) + (counts.get("d61_90") ?? 0);
  const atRisk = data.rows
    .filter((row) => row.bucket === "expired" || row.bucket === "d0_30")
    .reduce((sum, row) => sum + row.rentAmount, 0);

  return (
    <div>
      <ReportHeader
        title="Expiry schedule"
        question={`Every ${vocab.lease} with an end date, soonest first — a renewal agreed late is a void nobody planned for.`}
        subtitle={`${data.rows.length} with an end date · as at ${fmt.date(data.asOf)}`}
        actions={
          <Button variant="secondary" onClick={() => downloadCsv("leases")}>
            Export CSV
          </Button>
        }
      />

      {data.rows.length === 0 ? (
        <Card>
          <EmptyState title={`No ${vocab.lease} has an end date.`} />
        </Card>
      ) : (
        <div className="space-y-5">
          <FigureRow>
            <Figure label="With an end date" value={data.rows.length} />
            <Figure label="Already over" value={expired} tone={expired > 0 ? "bad" : "good"} sub="Neither renewed nor ended" />
            <Figure label="Within 90 days" value={soon} tone={soon > 0 ? "warn" : "ink"} />
            <Figure label={`${titleCase(vocab.rent)} at risk`} value={compact(atRisk)} sub="Over and within 30 days" />
          </FigureRow>

          <ChartCard
            title="When they end"
            hint="The bands get darker as they get closer, so the order reads without the colour. Anything already over is the darkest band and the one to act on first."
          >
            <CompositionBar label={`${titleCase(vocab.leases)} by when they end`} segments={segments} format={(value) => String(value)} />
            <SegmentKey segments={segments} format={(value) => String(value)} />
          </ChartCard>

          <Card padded={false}>
            <div className="p-5 sm:p-6">
              <CardTitle hint="Soonest first, and anything already over is at the top where it belongs.">The schedule</CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>{titleCase(vocab.tenant)}</th>
                  <th>{titleCase(vocab.unit)}</th>
                  <th>Ends</th>
                  <th>When</th>
                  <th className="num">{titleCase(vocab.rent)}</th>
                  <th></th>
                </tr>
              }
            >
              {data.rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/app/tenancies/${row.id}`} className="link">
                      {row.tenantName}
                    </Link>
                  </td>
                  <td className="text-ink-500">
                    {row.unitLabel}
                    <span className="block text-xs text-ink-400">{row.propertyName}</span>
                  </td>
                  <td className="text-ink-500">{fmt.date(row.endDate)}</td>
                  <td>
                    <ExpiryBadge endDate={row.endDate} asOf={data.asOf} />
                    <span className="ml-2 text-xs text-ink-400">
                      {row.daysLeft < 0 ? `${Math.abs(row.daysLeft)}d ago` : `in ${row.daysLeft}d`}
                    </span>
                  </td>
                  <td className="num tabular">{full(row.rentAmount)}</td>
                  <td>
                    <Link to={`/app/tenancies/${row.id}`} className="link text-xs">
                      Renew
                    </Link>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
